import { useShallow } from "zustand/react/shallow";
import { useAuthStore } from "stores/authStore";

/**
 * Hook de compatibilidad (antes Context API). La fuente de verdad es `useAuthStore`.
 */
export function useAuth() {
  return useAuthStore(
    useShallow((s) => {
      const role = s.role ?? s.auth?.role ?? null;
      const scopes = Array.isArray(s.auth?.scopes) ? s.auth.scopes : null;
      // Solo-lectura = tiene rol pero NO es owner/admin (o sus scopes no incluyen "*").
      // El contador entra acá: UI en modo consulta (sin emitir/borrar, solo prod).
      const isReadOnly =
        (role != null && role !== "owner" && role !== "admin") ||
        (scopes != null && !scopes.includes("*"));
      // Permiso puntual, leído de los scopes reales del backend en vez de
      // deducirlo del rol. `isReadOnly` sigue siendo cierto para el contador
      // —no emite ni borra— pero responder una factura de COMPRA sí puede.
      const lista = Array.isArray(scopes) ? scopes : [];
      // Gestionar documentos: remitir al SII, reparar con CAF, ocultar.
      // Sale del scope real y no del rol, porque `isReadOnly` sigue mandando
      // en lo que es de administración de la organización (miembros, API keys,
      // cambiar de ambiente) y ahí el contador no entra aunque pueda operar
      // documentos. Son dos cosas distintas y conviene que sigan separadas.
      const puedeGestionarDocumentos =
        (Array.isArray(scopes) ? scopes : []).some((x) => x === "*" || x === "manage_documents");

      const puedeResponderCompras =
        lista.includes("*")
        || lista.includes("manage_documents")
        || lista.includes("respond_purchases");

      // Reenviar un documento ya emitido al cliente. No emite, no anula y no
      // gasta folios: por eso el contador puede, aunque siga siendo solo-lectura
      // para todo lo demás.
      const puedeReenviarDocumentos =
        lista.includes("*")
        || lista.includes("manage_documents")
        || lista.includes("resend_documents");

      return {
        user: s.user,
        auth: s.auth,
        currentOrganization: s.currentOrganization,
        role,
        isReadOnly,
        puedeGestionarDocumentos,
        puedeResponderCompras,
        puedeReenviarDocumentos,
        loading: s.loading || !s.hasHydrated,
        isAuthenticated: Boolean(s.user),
        login: s.login,
        verifyLoginCode: s.verifyLoginCode,
        resendLoginCode: s.resendLoginCode,
        logout: s.logout,
        refreshSession: s.refreshSession,
      };
    }),
  );
}

export { useAuthStore } from "stores/authStore";
